import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

import CrewMemberCredentials from 'common/CrewMemberNameDrivercard';
import { SEARCH_CREW_MEMBER_BY_TEXT } from 'gql';

const CrewMemberAutoCompleteSingleV2 = ({
	defaultValue = null,
	onSelection = () => {},
	onReset = () => {},
	label = 'Search crew member',
	placeholder = 'Start typing (name or employee number)',
	optionsToDisplay = 5,
	subtitlePrefix = 'Employee number: ',
}) => {
	const [inputValue, setInputValue] = useState(defaultValue?.fullName ?? '');
	const [options, setOptions] = useState([]);

	const [getCrewMembersByText, { loading }] = useLazyQuery(SEARCH_CREW_MEMBER_BY_TEXT, {
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	useEffect(() => {
		setInputValue(defaultValue?.fullName ?? '');
	}, [defaultValue]);

	const selectedValue = useMemo(() => {
		if (!defaultValue?.id || !defaultValue?.fullName) return [];

		return [
			{
				title: defaultValue.fullName,
				value: defaultValue.id,
			},
		];
	}, [defaultValue]);

	const handleInputChange = (event) => {
		const nextValue = event?.target?.value ?? '';
		setInputValue(nextValue);

		if (nextValue === '') {
			setOptions([]);
			onReset();
		}
	};

	const handleDebounce = async (typedValue) => {
		if (!typedValue) {
			setOptions([]);
			return [];
		}

		try {
			const { data } = await getCrewMembersByText({
				variables: { text: typedValue },
			});

			const mappedOptions = (data?.crewMemberSearch?.results ?? []).map((driver) => ({
				title: driver.fullName,
				value: driver.id,
				subtitle: driver.driverCard,
				data: driver,
			}));

			setOptions(mappedOptions);
			return mappedOptions;
		} catch {
			setOptions([]);
			return [];
		}
	};

	const handleChange = (selectedOptions) => {
		const nextSelected = Array.isArray(selectedOptions) ? selectedOptions : [];

		if (nextSelected.length === 0) {
			setInputValue('');
			onReset();
			return;
		}

		const selectedOption = nextSelected[0];

		onSelection({
			id: selectedOption.value,
			fullName: selectedOption.title,
			driverCard: selectedOption.data?.driverCard,
		});

		setInputValue(selectedOption.title);
	};

	return (
		<Autocomplete
			{...(selectedValue.length ? { value: selectedValue } : {})}
			inputValue={inputValue}
			options={options}
			debounce={300}
			onDebounce={handleDebounce}
			onChange={handleChange}
			comboboxProps={{
				label,
				loading,
				placeholder,
				onChange: handleInputChange,
			}}
			listBoxProps={{
				label: 'members',
				optionsToDisplay,
				optionProps: (item) => ({
					renderOption: () => (
						<CrewMemberCredentials
							name={item.title}
							number={item.subtitle}
							prefix={subtitlePrefix}
						/>
					),
				}),
			}}
		/>
	);
};

export default CrewMemberAutoCompleteSingleV2;
